import { useEffect, useRef, useState } from "react";

import FinancialMonthAiResult from "./FinancialMonthAiResult";
import { isDemoMockAnalysis } from "./demoMockAnalysis";
import { isPresentationProject } from "../../../utils/presentationMode";
import {
  createFinancialMonthAiAnalysis,
  fetchAiAccess,
  fetchFinancialMonthAiHistory,
} from "./aiApi";

const QUESTIONS = [
  { key: "month_cash_variance", label: "Por que o pago ficou diferente da previsão de caixa?" },
  { key: "month_execution_variance", label: "O medido ficou acima ou abaixo do previsto?" },
  { key: "month_paid_vs_measured", label: "O pago está compatível com o medido?" },
  { key: "month_planning_check", label: "O que precisa ser revisto no planejamento?" },
];
const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const idempotencyKey = () => (
  globalThis.crypto?.randomUUID?.()
  ?? `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
);

const monthLabel = (competence) => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(competence ?? ""));
  if (!match) return "esta competência";
  return monthFormatter.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
};

const renewalDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(date);
};

const quotaText = (quota, period) => {
  if (!quota) return "Cota indisponível";
  if (quota.unlimited) return "Uso ilimitado";
  if (quota.remaining === 0) return "Cota utilizada neste período";
  const noun = quota.remaining === 1 ? "análise restante" : "análises restantes";
  return `${quota.remaining} ${noun} ${period === "monthly" ? "neste mês" : "nesta semana"}`;
};

const FinancialMonthAiPanel = ({ workId, competence, project, onClose }) => {
  const panelRef = useRef(null);
  const requestRef = useRef(null);
  const [access, setAccess] = useState(null);
  const [quota, setQuota] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [customQuestion, setCustomQuestion] = useState("");
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const presentationReadOnly = isPresentationProject(project);
  const contextKey = `${workId}:${competence}:${presentationReadOnly ? "read-only" : "mutable"}`;

  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    requestRef.current?.abort();
    requestRef.current = null;
    setAnalyzing(false);
    setCustomQuestion("");
  }, [contextKey]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setAccess(null);
    setQuota(null);
    setAnalysis(null);
    setError(null);
    Promise.all([
      fetchAiAccess(workId, { signal: controller.signal }),
      fetchFinancialMonthAiHistory(workId, competence, { signal: controller.signal }),
    ]).then(([accessPayload, historyPayload]) => {
      if (!active) return;
      setAccess(accessPayload);
      setQuota(accessPayload.quota);
      setAnalysis(historyPayload.analyses?.[0] ?? null);
    }).catch((caught) => {
      if (active && caught?.code !== "request_cancelled") setError(caught);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [contextKey, reloadKey]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const exhausted = quota?.unlimited === false && quota?.remaining === 0;
  const demoMock = isDemoMockAnalysis(analysis);
  const canAnalyze = access?.enabled === true
    && !exhausted
    && !analyzing
    && !demoMock
    && !presentationReadOnly;
  const quotaLabel = quotaText(quota, access?.plan?.period);

  const analyze = async (questionKey, question) => {
    if (!canAnalyze || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setAnalyzing(true);
    setError(null);
    try {
      const result = await createFinancialMonthAiAnalysis(workId, {
        competence,
        questionKey,
        ...(question ? { question } : {}),
      }, {
        idempotencyKey: idempotencyKey(),
        signal: controller.signal,
      });
      setAnalysis(result.analysis);
      setQuota(result.quota);
      if (questionKey === "custom") setCustomQuestion("");
    } catch (caught) {
      if (caught?.code !== "request_cancelled") setError(caught);
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setAnalyzing(false);
      }
    }
  };

  return (
    <section
      className="item-ai-panel financial-month-ai-panel"
      ref={panelRef}
      aria-label={`Análise com IA de ${monthLabel(competence)}`}
    >
      <header className="item-ai-header">
        <div>
          <span className="item-ai-eyebrow">Ampli Obras com IA</span>
          <h3>Entenda {monthLabel(competence)}</h3>
          <p>Compare o previsto, o medido e os pagamentos antes de atualizar o planejamento.</p>
        </div>
        <button type="button" className="item-ai-close" onClick={onClose}>Fechar análise</button>
      </header>

      {loading ? <p className="item-ai-loading" role="status">Preparando os dados desta competência…</p> : null}
      {!loading && access ? (
        <>
          {access.enabled ? (
            <>
              <div className={`item-ai-quota${exhausted ? " item-ai-quota--exhausted" : ""}`}>
                <strong>{quotaLabel}</strong>
                <span>A cota é compartilhada por todos os usuários desta obra e por todos os usos da IA.</span>
              </div>
              {presentationReadOnly ? (
                <p
                  aria-label="Análise somente leitura"
                  className="item-ai-provider-warning item-ai-provider-warning--demo"
                  role="status"
                >
                  <strong>Obra de apresentação: histórico somente leitura.</strong>
                  <span>Novas análises estão desabilitadas e nenhuma cota será consumida.</span>
                </p>
              ) : demoMock ? (
                <p
                  aria-label="Análise demonstrativa somente leitura"
                  className="item-ai-provider-warning item-ai-provider-warning--demo"
                  role="status"
                >
                  <strong>Esta análise demonstrativa não consumiu cota.</strong>
                  <span>Novas análises estão bloqueadas nesta demonstração.</span>
                </p>
              ) : (
                <>
                  <div className="item-ai-questions" role="group" aria-label="Perguntas sugeridas sobre o mês">
                    {QUESTIONS.map((question) => (
                      <button
                        key={question.key}
                        type="button"
                        disabled={!canAnalyze}
                        onClick={() => analyze(question.key)}
                      >
                        {question.label}
                      </button>
                    ))}
                  </div>
                  <div className="item-ai-custom-question">
                    <label htmlFor={`financial-month-ai-custom-${competence}`}>
                      Ou faça uma pergunta sobre esta competência
                    </label>
                    <div>
                      <textarea
                        id={`financial-month-ai-custom-${competence}`}
                        value={customQuestion}
                        maxLength={500}
                        disabled={!canAnalyze}
                        onChange={(event) => setCustomQuestion(event.target.value)}
                        placeholder="Ex.: quais pagamentos devo conferir antes de replanejar?"
                      />
                      <button
                        type="button"
                        disabled={!canAnalyze || !customQuestion.trim()}
                        onClick={() => analyze("custom", customQuestion.trim())}
                      >
                        Analisar pergunta
                      </button>
                    </div>
                  </div>
                  {analyzing ? (
                    <p className="item-ai-loading" role="status">
                      Analisando previsão, medição, pagamentos e direcionadores…
                    </p>
                  ) : null}
                  {exhausted ? (
                    <p className="item-ai-upsell">
                      Plano {access.plan?.label || "atual"}
                      {renewalDate(quota?.resetAt) ? ` · renova em ${renewalDate(quota.resetAt)}` : ""}.
                      {" "}Fale com a Ampli para ampliar o plano. Seu histórico continua disponível.
                    </p>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <p className="item-ai-disabled" role="status">
              <strong>IA não habilitada para esta obra</strong>
              <span>O acesso pode ser liberado no Backoffice sem alterar o Ampli Obras.</span>
            </p>
          )}
        </>
      ) : null}

      {error ? (
        <div className="item-ai-error" role="alert">
          <span>{error.message}</span>
          {!access ? (
            <button type="button" onClick={() => setReloadKey((value) => value + 1)}>
              Tentar carregar novamente
            </button>
          ) : null}
        </div>
      ) : null}
      <FinancialMonthAiResult
        analysis={analysis}
        readOnly={presentationReadOnly}
        workId={workId}
      />
    </section>
  );
};

export default FinancialMonthAiPanel;
